import {
  JobFailureError,
  JobPollingCancelledError,
  JobPollingTimeoutError,
} from "./poll-job-status";

/**
 * Error types for async job operations
 */
export enum AsyncJobErrorType {
  /** 4xx validation/client errors during job enqueue */
  ENQUEUE_VALIDATION = "ENQUEUE_VALIDATION",
  /** 5xx server errors during job enqueue */
  ENQUEUE_SERVER = "ENQUEUE_SERVER",
  /** Job failed during execution (status === "FAILURE") */
  JOB_FAILURE = "JOB_FAILURE",
  /** Job polling timed out */
  POLLING_TIMEOUT = "POLLING_TIMEOUT",
  /** Job polling was cancelled */
  POLLING_CANCELLED = "POLLING_CANCELLED",
  /** Network/connectivity error during polling */
  NETWORK_ERROR = "NETWORK_ERROR",
  /** Unknown/unexpected error */
  UNKNOWN = "UNKNOWN",
}

/**
 * Classified error result
 */
export interface ClassifiedError {
  type: AsyncJobErrorType;
  message: string;
  originalError: Error;
  isRecoverable: boolean;
  shouldRetry: boolean;
  userMessage: string;
}

/**
 * Classify an error from async job operations
 *
 * This function helps differentiate between:
 * - Enqueue validation errors (4xx) - usually user input issues
 * - Enqueue server errors (5xx) - backend problems
 * - Job execution failures - job ran but failed
 * - Polling issues - timeout, network errors
 * - Cancellations - user-initiated
 *
 * @param error - The error to classify
 * @returns Classified error with type, message, and recovery info
 */
export function classifyAsyncJobError(error: unknown): ClassifiedError {
  const errorObj = error instanceof Error ? error : new Error(String(error));

  // Job-specific errors
  if (error instanceof JobFailureError) {
    return {
      type: AsyncJobErrorType.JOB_FAILURE,
      message: errorObj.message,
      originalError: errorObj,
      isRecoverable: false,
      shouldRetry: false,
      userMessage:
        "The operation failed during processing. Please check your data and try again.",
    };
  }

  if (error instanceof JobPollingTimeoutError) {
    return {
      type: AsyncJobErrorType.POLLING_TIMEOUT,
      message: errorObj.message,
      originalError: errorObj,
      isRecoverable: true,
      shouldRetry: true,
      userMessage:
        "The operation is taking longer than expected. It may still complete in the background. Please check your recent jobs or try again.",
    };
  }

  if (error instanceof JobPollingCancelledError) {
    return {
      type: AsyncJobErrorType.POLLING_CANCELLED,
      message: errorObj.message,
      originalError: errorObj,
      isRecoverable: true,
      shouldRetry: true,
      userMessage: "Operation cancelled. You can try again if needed.",
    };
  }

  // HTTP errors (enqueue phase)
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const data = error.response?.data;

    if (status && status >= 400 && status < 500) {
      // Client/validation error
      const validationMessage = extractValidationMessage(data);
      return {
        type: AsyncJobErrorType.ENQUEUE_VALIDATION,
        message: validationMessage || errorObj.message,
        originalError: errorObj,
        isRecoverable: true,
        shouldRetry: false,
        userMessage:
          validationMessage ||
          "Invalid request. Please check your data and try again.",
      };
    }

    if (status && status >= 500) {
      // Server error
      return {
        type: AsyncJobErrorType.ENQUEUE_SERVER,
        message: errorObj.message,
        originalError: errorObj,
        isRecoverable: true,
        shouldRetry: true,
        userMessage:
          "Server error occurred. Please try again in a few moments.",
      };
    }

    // Network error (no response)
    if (!error.response) {
      return {
        type: AsyncJobErrorType.NETWORK_ERROR,
        message: errorObj.message,
        originalError: errorObj,
        isRecoverable: true,
        shouldRetry: true,
        userMessage:
          "Network error. Please check your connection and try again.",
      };
    }
  }

  // Generic network errors
  if (
    errorObj.message.includes("network") ||
    errorObj.message.includes("fetch") ||
    errorObj.message.includes("ECONNREFUSED") ||
    errorObj.message.includes("timeout")
  ) {
    return {
      type: AsyncJobErrorType.NETWORK_ERROR,
      message: errorObj.message,
      originalError: errorObj,
      isRecoverable: true,
      shouldRetry: true,
      userMessage:
        "Network error occurred. Please check your connection and try again.",
    };
  }

  // Unknown error
  return {
    type: AsyncJobErrorType.UNKNOWN,
    message: errorObj.message,
    originalError: errorObj,
    isRecoverable: false,
    shouldRetry: false,
    userMessage:
      "An unexpected error occurred. Please try again or contact support if the issue persists.",
  };
}

/**
 * Check if an error is an Axios error (duck typing)
 */
function isAxiosError(error: any): error is {
  response?: { status?: number; data?: any };
  message: string;
} {
  return error && typeof error === "object" && "response" in error;
}

/**
 * Extract user-friendly validation message from error response data
 */
function extractValidationMessage(data: any): string | null {
  if (!data) return null;

  // Common Django REST Framework error formats
  if (typeof data === "string") return data;

  if (data.detail) return String(data.detail);

  if (data.error) return String(data.error);

  if (data.message) return String(data.message);

  // Field-specific errors
  if (typeof data === "object") {
    const errors: string[] = [];
    for (const [field, messages] of Object.entries(data)) {
      if (Array.isArray(messages)) {
        errors.push(`${field}: ${messages.join(", ")}`);
      } else {
        errors.push(`${field}: ${String(messages)}`);
      }
    }
    if (errors.length > 0) {
      return errors.join("; ");
    }
  }

  return null;
}

/**
 * Get a user-friendly error message for toast notifications
 *
 * @param error - The error to get message for
 * @param context - Optional context string (e.g., "parsing", "uploading")
 * @returns User-friendly error message
 */
export function getErrorMessage(error: unknown, context?: string): string {
  const classified = classifyAsyncJobError(error);

  if (context) {
    return `Error ${context}: ${classified.userMessage}`;
  }

  return classified.userMessage;
}

/**
 * Determine if an error should trigger a retry
 */
export function shouldRetryError(error: unknown): boolean {
  const classified = classifyAsyncJobError(error);
  return classified.shouldRetry;
}

/**
 * Determine if an error is recoverable (user can try again)
 */
export function isRecoverableError(error: unknown): boolean {
  const classified = classifyAsyncJobError(error);
  return classified.isRecoverable;
}
